import { FaPlus, FaCheckCircle, FaFilter } from "react-icons/fa";
import { AddDimensionModal } from "../pop-ups/add-dimension-popup";
import { useEffect, useRef, useState } from "react";
import "../styles/commerce.css";

export const CommerceDimensionTable = ({
  title,
  statusFilter,
  addButton = false,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  columns,
  columnDefinitions = [],
  data,
  getRowValues,
  onRowClick,
  sourceFilter,
  variant = "main",
  addConfig,
  close,
  columnFilters = {},
  onColumnFilterChange,
}) => {
  const [addModalOpen, setIsAddModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const filterWrapperRef = useRef(null);

  useEffect(() => {
    if (!showFilters) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (!filterWrapperRef.current?.contains(event.target)) {
        setShowFilters(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFilters]);

  const isLookup = variant === "lookup";
  const columnCount = columns.length;

  const isVarcharColumn = (index) => {
    const type = String(columnDefinitions[index]?.type ?? "")
      .trim()
      .toLowerCase();

    return ["text", "string", "varchar", "nvarchar", "char", "nchar"]
      .some((textType) => type === textType || type.startsWith(`${textType}(`));
  };

  const tableStyle = {
    "--cols": columnCount,
  };

  const hasActiveFilters = Object.values(columnFilters).some(
    (value) =>
      value !== null &&
      value !== undefined &&
      value !== ""
  );

  const formatFilterValue = (value) => {
    if (String(value) === "true") {
      return "Yes";
    }

    if (String(value) === "false") {
      return "No";
    }

    return value;
  };

  const handleAddModalClose = (shouldRefresh = false) => {
    setIsAddModalOpen(false);

    if (shouldRefresh === true) {
      close?.(true);
    }
  };

  return (
    <div className={`commerce-card ${isLookup ? "commerce-card-lookup" : "commerce-card-main"}`}
    >
      <div className="commerce-card-header">
        <div className="commerce-title-row">
          <h2>{title}</h2>
          {!isLookup && statusFilter}
        </div>

        <div className="commerce-search-row">
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="commerce-search"
          />
          {!isLookup && (
            <div
              ref={filterWrapperRef}
              className="commerce-filter-wrapper"
            >
              <button
                type="button"
                className={`commerce-filter-button ${hasActiveFilters ? "active" : ""}`}
                onClick={() =>
                  setShowFilters((previous) => !previous)
                }
              >
                <FaFilter />
              </button>

              {showFilters && (
                <div className="commerce-filter-popover">
                  {columns.map((column, index) => {
                    const options = [
                      ...new Set(
                        data
                          .map(
                            (item) =>
                              getRowValues(item)[index]
                          )
                          .filter(
                            (value) =>
                              value !== null &&
                              value !== undefined &&
                              value !== ""
                          )
                          .map((value) =>
                            String(value)
                          )
                      ),
                    ].sort((a, b) =>
                      a.localeCompare(b, undefined, {
                        numeric: true,
                        sensitivity: "base",
                      })
                    );

                    return (
                      <div
                        key={index}
                        className="commerce-filter-field"
                      >
                        <label>{column}</label>

                        <select
                          value={
                            columnFilters[index] ?? ""
                          }
                          onChange={(e) =>
                            onColumnFilterChange?.(
                              index,
                              e.target.value
                            )
                          }
                          className="commerce-column-filter"
                        >
                          <option value="">
                            All {column}
                          </option>

                          {options.map((value) => (
                            <option
                              key={value}
                              value={value}
                            >
                              {formatFilterValue(value)}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {addButton && (
            <button
              type="button"
              className="commerce-add-btn"
              onClick={() =>
                setIsAddModalOpen(true)
              }
            >
              <FaPlus />
            </button>
          )}
        </div>

        {hasActiveFilters && (
          <div className="commerce-active-filters">
            {columns.map((column, index) => {
              const value = columnFilters[index];

              if (
                value === null ||
                value === undefined ||
                value === ""
              ) {
                return null;
              }

              return (
                <div
                  key={index}
                  className="commerce-filter-chip"
                >
                  <span className="commerce-filter-chip-label">
                    {column}:
                  </span>

                  <span>
                    {formatFilterValue(value)}
                  </span>

                  <button
                    type="button"
                    className="commerce-filter-chip-remove"
                    onClick={() =>
                      onColumnFilterChange?.(
                        index,
                        ""
                      )
                    }
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {!isLookup && sourceFilter}

      <div
        className={
          isLookup
            ? "lookup-table"
            : "commerce-table"
        }
        style={tableStyle}
      >
        <div className="commerce-table-header">
          {columns.map((column, index) => (
            <span
              key={index}
              className={isVarcharColumn(index) ? "commerce-varchar-column" : undefined}
            >
              {column}
            </span>
          ))}

          <span />
        </div>

        <div className="commerce-table-body">
          {data.map((item, index) => (
            <div
              className="commerce-table-row"
              key={item.key ?? index}
              onClick={() => onRowClick?.(item)}
            >
              {getRowValues(item).map(
                (value, valueIndex) => (
                  <span
                    key={valueIndex}
                    className={isVarcharColumn(valueIndex) ? "commerce-varchar-column" : undefined}
                  >
                    {typeof value === "boolean" ? (
                      value ? (
                        <FaCheckCircle
                          color="green"
                          fontSize={20}
                        />
                      ) : (
                        "-"
                      )
                    ) : (
                      value || "-"
                    )}
                  </span>
                )
              )}

              <button
                className="commerce-row-menu"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRowClick?.(item);
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {addButton && addConfig && (
        <AddDimensionModal
          isOpen={addModalOpen}
          config={addConfig}
          onClose={handleAddModalClose}
        />
      )}
    </div>
  );
};
